// src/pages/admin/DisputeListPage.tsx
// ============================================================
// Summit League v2 — Dispute List Page
//
// Filterable, sortable dispute table for admin panel.
// Uses existing GET /api/disputes endpoint with query params.
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';
import DisputeFilters from '../../components/admin/DisputeFilters';
import DisputeStatusBadge from '../../components/admin/DisputeStatusBadge';

import '../../styles/admin-disputes.css';

type DisputeStatus = 'open' | 'under_review' | 'resolved_team_a' | 'resolved_team_b' | 'resolved_draw' | 'dismissed';

interface DisputeListItem {
  disputeId: number;
  matchId: number;
  matchGameId: number | null;
  match: {
    matchId: number;
    gameName: string;
    gameCode: string;
    seasonName: string;
    teamA: { teamId: number; teamName: string; teamTag: string | null };
    teamB: { teamId: number; teamName: string; teamTag: string | null } | null;
    matchFormat: string;
  };
  isAutoCreated: boolean;
  reason: string | null;
  status: DisputeStatus;
  createdAt: string;
}

interface DisputeListResponse {
  disputes: DisputeListItem[];
  total: number;
  limit: number;
  offset: number;
}

const PAGE_SIZE = 20;

export default function DisputeListPage() {
  const navigate = useNavigate();

  const [disputes, setDisputes] = useState<DisputeListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Filters
  const [gameId, setGameId] = useState<number | undefined>();
  const [seasonId, setSeasonId] = useState<number | undefined>();
  const [statuses, setStatuses] = useState<DisputeStatus[]>(['open', 'under_review']);

  // Sort
  const [sortField, setSortField] = useState<'created_at' | 'status'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const fetchDisputes = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (gameId) params.append('gameId', String(gameId));
      if (seasonId) params.append('seasonId', String(seasonId));
      if (statuses.length > 0) params.append('status', statuses.join(','));
      params.append('limit', String(PAGE_SIZE));
      params.append('offset', String(offset));

      const res = await axiosClient.get<DisputeListResponse>(`/disputes?${params.toString()}`);
      setDisputes(res.data.disputes);
      setTotal(res.data.total);
    } catch (err) {
      console.error('[DisputeList] Failed to fetch:', err);
    } finally {
      setIsLoading(false);
    }
  }, [gameId, seasonId, statuses, offset]);

  useEffect(() => {
    fetchDisputes();
  }, [fetchDisputes]);

  // Reset offset when filters change
  useEffect(() => {
    setOffset(0);
  }, [gameId, seasonId, statuses]);

  const handleSort = (field: 'created_at' | 'status') => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Client-side sort (backend sorts by created_at DESC by default)
  const sortedDisputes = [...disputes].sort((a, b) => {
    const dir = sortOrder === 'asc' ? 1 : -1;
    if (sortField === 'created_at') {
      return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir;
    }
    return a.status.localeCompare(b.status) * dir;
  });

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const getSortIcon = (field: string) => {
    if (sortField !== field) return '↕';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="sl-admin-disputes">
      <div className="sl-admin-page-header">
        <h1 className="sl-admin-page-title">Disputes</h1>
        <p className="sl-admin-page-subtitle">
          {total} total dispute{total !== 1 ? 's' : ''}
        </p>
      </div>

      <DisputeFilters
        selectedGameId={gameId}
        selectedSeasonId={seasonId}
        selectedStatuses={statuses}
        onGameChange={setGameId}
        onSeasonChange={setSeasonId}
        onStatusChange={setStatuses}
      />

      <div className="sl-dispute-table-wrapper">
        {isLoading ? (
          <div className="sl-admin-loading-inline">Loading disputes...</div>
        ) : disputes.length === 0 ? (
          <div className="sl-admin-empty">
            <span className="sl-admin-empty-icon">✓</span>
            <p className="sl-admin-empty-text">No disputes match your filters</p>
          </div>
        ) : (
          <>
            <table className="sl-dispute-table">
              <thead>
                <tr>
                  <th className="sl-dispute-th">ID</th>
                  <th className="sl-dispute-th">Match</th>
                  <th className="sl-dispute-th">Game</th>
                  <th
                    className="sl-dispute-th sl-dispute-th--sortable"
                    onClick={() => handleSort('status')}
                  >
                    Status {getSortIcon('status')}
                  </th>
                  <th
                    className="sl-dispute-th sl-dispute-th--sortable"
                    onClick={() => handleSort('created_at')}
                  >
                    Created {getSortIcon('created_at')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedDisputes.map((d) => (
                  <tr
                    key={d.disputeId}
                    className="sl-dispute-row"
                    onClick={() => navigate(`/admin/disputes/${d.disputeId}`)}
                  >
                    <td className="sl-dispute-td sl-dispute-td--id">#{d.disputeId}</td>
                    <td className="sl-dispute-td">
                      <div className="sl-dispute-match-info">
                        <span className="sl-dispute-teams">
                          {d.match.teamA.teamName}
                          {d.match.teamB ? ` vs ${d.match.teamB.teamName}` : ''}
                        </span>
                        <span className="sl-dispute-format">{d.match.matchFormat.toUpperCase()}</span>
                      </div>
                    </td>
                    <td className="sl-dispute-td">
                      <span className="sl-dispute-game">{d.match.gameName}</span>
                      <span className="sl-dispute-season">{d.match.seasonName}</span>
                    </td>
                    <td className="sl-dispute-td">
                      <DisputeStatusBadge status={d.status} />
                    </td>
                    <td className="sl-dispute-td sl-dispute-td--date">
                      {new Date(d.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="sl-dispute-pagination">
                <button
                  type="button"
                  className="sl-dispute-pagination-btn"
                  disabled={currentPage <= 1}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  Previous
                </button>
                <span className="sl-dispute-pagination-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  className="sl-dispute-pagination-btn"
                  disabled={currentPage >= totalPages}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
